import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { BaseInfoCompProps, capitalizeAll, FieldInputType, ITEMS_PER_PAGE_5, ITEMS_PER_PAGE_LIST, SelectInputOption }
    from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, setIsEditingForm, showErrorNotification } from '@moderepo/mode-ui-state';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import produce, { Draft } from 'immer';
import { Agent } from '@moderepo/mode-internal-apis';
import { Entity } from '@moderepo/mode-apis';
import { DashboardWidgetType, DASHBOARD_WIDGET_MIN_WIDTH, DASHBOARD_WIDGET_MIN_HEIGHT } from './dashboardTypes';
import { useRitzTranslation } from '../../utils/customHooks';
import { createNewHomeDashboardWidget, createNewProjectDashboardWidget, DashboardLogicApiCallResponse, updateHomeDashboardWidget,
    updateProjectDashboardWidget } from './dashboardLogic';
import { AlertsDashboardWidgetSettingsComp, AlertsDashboardWidgetSettingsCompProps } from '../../components';
import { AlertsDashboardWidgetInputErrors, AlertsDashboardWidgetInputs, AlertsDashboardWidgetSettings, AlertsDashboardWidgetViewType,
    validateAlertsDashboardWidgetInputs, WIDGET_GROUP_WIDGET_MIN_HEIGHT } from '.';
import { createIconOptions } from './dashboardUtils';
import { createHomeOptions } from '../../utils/utils';
import { useGetAllProjectHomes, useSelectEntity } from '../../utils/dataStateHooks';
import { AlertsTableHiddenFields } from '../alerting';


const useStyle = makeStyles(() => {
    return {
        widget: {
            width: '100%',
        },
    };
});


/**
 * Initialize the Alerts Dashboard Widget inputs with the given existing widget object or default values if there is not widget provided
 */
const initializeUserInputs = (
    widget?: AlertsDashboardWidgetSettings | undefined,
    homeId?: number | undefined,
    entity?: Entity | undefined,
): AlertsDashboardWidgetInputs => {
    return {
        title      : widget?.title,
        icon       : widget ? widget.icon : 'notifications_active',
        homeId     : widget ? widget.homeId : homeId ?? 0,
        entity,
        viewType   : widget ? widget.viewSettings.viewType : AlertsDashboardWidgetViewType.TABULAR,
        pageSize   : widget ? widget.viewSettings.pageSize : ITEMS_PER_PAGE_5,
        hideColumns: widget?.viewSettings.hideColumns ?? [],
    };
};


export interface AlertsDashboardWidgetSettingsContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;
    readonly selectedWidget?: AlertsDashboardWidgetSettings | undefined;


    // The ID of the widget, type WidgetGroup, to place the new widget into. This is OPTIONAL and only for creating new widget.
    readonly widgetGroupId?: number | undefined;

    // Only available if this container is used for creating new widget
    readonly newWidgetHandler?: {
        readonly onNewWidgetCreated: ()=> void;
        readonly onNewWidgetCanceled: ()=> void;
    } | undefined;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly homeId?: number | undefined;
    }
}


/**
 * This is a wrapper for the ValueDashboardWidgetSettings component. It contains all the logic for that component.
 */
export const AlertsDashboardWidgetSettingsContainer: React.FC<AlertsDashboardWidgetSettingsContainerProps> = (
    props: AlertsDashboardWidgetSettingsContainerProps,
) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;

    // Get all Project's homes
    const allHomes = useGetAllProjectHomes(props.projectId);

    // Get the Entity object for the entityId set in the selectedWidget
    const selectedEntity = useSelectEntity(
        props.projectId,
        props.selectedWidget?.entityId,
        false,
    );


    // A cached values of the user inputs. Usually we don't need to cache the user inputs but we do need it in this case because we need to load
    // additional data based on the user inputs
    const [currentUserInputs, setCurrentUserInputs] = useState<AlertsDashboardWidgetInputs>(initializeUserInputs(
        props.selectedWidget, props.routeContext?.homeId, selectedEntity,
    ));


    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const isNewWidget = props.selectedWidget === undefined;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<AlertsDashboardWidgetInputErrors>({ });


    /**
     * Drop down options to be used in the Icon input field
     */
    const iconInputOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createIconOptions(true, capitalizeAll(trans('label.none')));
    }, [trans]);


    /**
     * This useEffect is used for re-initializing the currentUserInputs when props.selectedWidget changed. useState WIll NOT re-initialize
     * the currentUserInputs therefore we need useEffect to do it. If we don't re-initialize the inputs, the settings form will stay the same
     * even when the user selected a different widget.
     */
    useEffect(() => {
        setCurrentUserInputs(initializeUserInputs(props.selectedWidget, props.routeContext?.homeId, selectedEntity));
    }, [props.routeContext?.homeId, props.selectedWidget, selectedEntity]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.widgetId is undefined which is when user want to create a Widget
     */
    useEffect(() => {
        if (isNewWidget) {
            onChangeEditingMode(true);
        }
    }, [isNewWidget, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
        });

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newWidgetHandler !== undefined) {
            props.newWidgetHandler.onNewWidgetCanceled();
        }

        // Reset the cached user input with the selectedWidget's data
        setCurrentUserInputs(initializeUserInputs(props.selectedWidget, props.routeContext?.homeId, selectedEntity));
    }, [closeEditing, props.newWidgetHandler, props.routeContext?.homeId, props.selectedWidget, selectedEntity]);



    /**
     * Create a new widget
     */
    const createNewWidget = useCallback(async (inputs: AlertsDashboardWidgetInputs) => {

        // Validate inputs and get the set of errors
        const newErrors = validateAlertsDashboardWidgetInputs(inputs);
        setInputErrors(newErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });


        if (!hasErrors && inputs.title && inputs.viewType) {

            // Create a new widget using the provided input and add it to the Home Dashboard KV
            const newWidget: AlertsDashboardWidgetSettings = {
                id           : Date.now(),
                type         : DashboardWidgetType.ALERTS,
                widgetGroupId: props.widgetGroupId,
                layouts      : {
                    lg: {
                        x: null,        // Give the new Widget null value for X/Y position since they don't have a position yet. We will not give
                        y: null,        // them value 0/0 because there might already be a widget at location 0/0.
                        w: DASHBOARD_WIDGET_MIN_WIDTH * 2,
                        h: (props.widgetGroupId ? WIDGET_GROUP_WIDGET_MIN_HEIGHT : DASHBOARD_WIDGET_MIN_HEIGHT) * 2,
                    },
                },
                title       : inputs.title,
                icon        : inputs.icon,
                homeId      : inputs.homeId ? inputs.homeId : undefined,
                entityId    : inputs.entity?.entityId,
                viewSettings: {
                    viewType   : inputs.viewType,
                    pageSize   : inputs.pageSize,
                    hideColumns: inputs.hideColumns,
                },
            };

            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    // Dashboard created successfully
                    closeEditing();

                    // Notify the handler a new widget is added
                    if (props.newWidgetHandler !== undefined) {
                        props.newWidgetHandler.onNewWidgetCreated();
                    }
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                }
            };

            if (props.routeContext?.homeId) {
                createNewHomeDashboardWidget(appDataState, appDataDispatch, props.routeContext.homeId, props.dashboardId, newWidget)
                    .then(onCompleted);
            } else {
                createNewProjectDashboardWidget(dataState, dataDispatch, props.projectId, loggedInAgent.id, props.dashboardId, newWidget)
                    .then(onCompleted);
            }
        }

    }, [props.widgetGroupId, props.routeContext?.homeId, props.newWidgetHandler, props.dashboardId, props.projectId, closeEditing, uiDispatch, trans,
        appDataState, appDataDispatch, dataState, dataDispatch, loggedInAgent.id]);



    /**
     * Updating an existing widget
     */
    const updateWidget = useCallback(async (inputs: AlertsDashboardWidgetInputs, widget: AlertsDashboardWidgetSettings) => {

        // Validate inputs and get the set of errors
        const newErrors = validateAlertsDashboardWidgetInputs(inputs);
        setInputErrors(newErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });

        // If there is no error, make an API call to update the widget
        if (!hasErrors && inputs.title && inputs.viewType) {

            // Make a copy of the current 'widget' and replace the editable props with the values from user inputs
            const updatedWidget: AlertsDashboardWidgetSettings = {
                ...widget,
                type        : DashboardWidgetType.ALERTS,
                title       : inputs.title ?? widget.title,
                icon        : inputs.icon,
                homeId      : inputs.homeId ? inputs.homeId : undefined,
                entityId    : inputs.entity?.entityId,
                viewSettings: {
                    viewType   : inputs.viewType,
                    pageSize   : inputs.pageSize,
                    hideColumns: inputs.hideColumns,
                },
            };


            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    // Dashboard updated successfully
                    closeEditing();
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                }
            };

            if (props.routeContext?.homeId) {
                updateHomeDashboardWidget(appDataState, appDataDispatch, props.routeContext.homeId, props.dashboardId, updatedWidget)
                    .then(onCompleted);
            } else {
                updateProjectDashboardWidget(dataState, dataDispatch, props.projectId, loggedInAgent.id, props.dashboardId, updatedWidget)
                    .then(onCompleted);
            }
        }
    }, [appDataDispatch, appDataState, closeEditing, dataDispatch, dataState, loggedInAgent.id, props.dashboardId, props.projectId,
        props.routeContext, trans, uiDispatch]);



    /**
     * On Edit/Create widget form submit event. Update the widget or create a new widget and save the widget settings in Home Dashboard
     */
    const onEditWidgetFormSubmit = useCallback(async (inputs: AlertsDashboardWidgetInputs) => {
        if (props.selectedWidget === undefined) {
            createNewWidget(inputs);
        } else {
            updateWidget(inputs, props.selectedWidget);
        }
    }, [createNewWidget, props.selectedWidget, updateWidget]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onEditWidgetFormInputChange = useCallback((updatedFieldName: string, newValue: any) => {

        // Convert 'name' from just any string to a more strict name from this object AlertsDashboardWidgetSettingsCompProps['fields']
        // This will make sure we don't misspell the name below when we try to check which field was updated. The compiler will throw error
        // if we try to compare filedName to some random string that is not part of AlertsDashboardWidgetSettingsCompProps['fields'].
        const fieldName = updatedFieldName as (
            keyof AlertsDashboardWidgetSettingsCompProps |
            keyof AlertsDashboardWidgetSettingsCompProps['fields']['view']
        );


        // since the input error object use the same field name as the input field, we can do it this way
        setInputErrors(produce(inputErrors, (draft: Draft<AlertsDashboardWidgetInputs>) => {
            draft[fieldName] = undefined;
        }));


        // For all other field, it will be a straightforward update, replace the value of the 'fieldName' field with the new value
        setCurrentUserInputs(produce(currentUserInputs, (draft: Draft<AlertsDashboardWidgetInputs>) => {
            // update the value of the field that changed
            draft[fieldName] = newValue;
        }));
    }, [currentUserInputs, inputErrors]);


    /**
     * Drop down options to be used in the Home ID input field
     */
    const homeInputOptions = useMemo((): readonly SelectInputOption<number>[] => {
        const options = createHomeOptions(allHomes);
        // Because HomeId is optional, we will add a 'None' option to the list.
        return [
            {
                value: 0,
                label: capitalizeAll(trans('label.none')),
            },
            ...options,
        ];
    }, [allHomes, trans]);


    /**
     * Drop down options to be used in the Page Size input field
     */
    const pageSizeInputOptions = useMemo((): readonly SelectInputOption<number>[] => {
        return ITEMS_PER_PAGE_LIST.map((value) => {
            return {
                label: value.toString(),
                value,
            };
        });
    }, []);


    /**
     * Drop down options to be used in the viewType input field
     */
    const viewTypeInputOptions = useMemo((): readonly SelectInputOption<AlertsDashboardWidgetViewType>[] => {
        return [{
            label: capitalizeAll(trans('dashboard.alerts_widget_view_type.tabular')),
            value: AlertsDashboardWidgetViewType.TABULAR,
        }, {
            label: capitalizeAll(trans('dashboard.alerts_widget_view_type.map')),
            value: AlertsDashboardWidgetViewType.MAP,
        }];
    }, [trans]);


    /**
     * Create the set of input fields for the alert dashboard widget settings
     */
    const dashboardWidgetCompFields = useMemo((): AlertsDashboardWidgetSettingsCompProps['fields'] => {
        const widget = props.selectedWidget;

        /**
         * The options for the set of Alerts table columns the user can hide. We will not includes the "description", "state", and "severity"
         * columns because those columns are important and doesn't make sense to be hidden.
         */
        const hiddenColumnOptions: readonly SelectInputOption<keyof AlertsTableHiddenFields>[] = [
            {
                label: capitalizeAll(trans('label.id')),
                value: 'id',
            },
            {
                label: capitalizeAll(trans('alerting.label.invocation_date')),
                value: 'invocationTime',
            },
            {
                label: capitalizeAll(trans('alerting.label.recovered_date')),
                value: 'recoveryTime',
            },
            {
                label: capitalizeAll(trans('field.entity.label')),
                value: 'entity',
            },
            {
                label: capitalizeAll(trans('alerting.alert_condition_type')),
                value: 'conditionType',
            },
        ];



        return {
            title: {
                label   : capitalizeAll(trans('label.title')),
                value   : widget ? widget.title : '',
                editable: true,
                required: true,
                error   : inputErrors.title ? trans(inputErrors.title) : undefined,
            },
            icon: {
                label      : capitalizeAll(trans('label.icon')),
                value      : widget ? widget.icon : '',
                controlType: FieldInputType.AUTOCOMPLETE,
                options    : iconInputOptions,
                editable   : true,
                error      : inputErrors.icon ? trans(inputErrors.icon) : undefined,
            },
            homeId: {
                label   : capitalizeAll(trans('label.home_id')),
                value   : currentUserInputs.homeId ?? 0,
                editable: props.routeContext?.homeId === undefined,
                required: false,
                options : homeInputOptions,
                hidden  : props.routeContext?.homeId !== undefined,
                error   : inputErrors.homeId ? trans(inputErrors.homeId) : undefined,
            },
            entity: {
                label                        : capitalizeAll(trans('field.entity.label')),
                value                        : currentUserInputs.entity,
                editable                     : true,
                required                     : false,
                error                        : inputErrors.entity ? trans(inputErrors.entity) : undefined,
                entitiesListingContainerProps: {
                    projectId: props.projectId,
                    homeId   : currentUserInputs.homeId,
                },
                texts: {
                    clearButtonLabel : capitalizeAll(trans('label.clear')),
                    cancelButtonLabel: capitalizeAll(trans('label.cancel')),
                    selectButtonLabel: capitalizeAll(trans('label.select')),
                    emptyValueDisplay: capitalizeAll(trans('label.none')),
                },
            },
            view: {
                viewType: {
                    label   : capitalizeAll(trans('dashboard.labels.alerts_widget_view_type')),
                    value   : currentUserInputs.viewType,
                    required: true,
                    editable: true,
                    options : viewTypeInputOptions,
                    error   : inputErrors.viewType ? trans(inputErrors.viewType) : undefined,
                },
                pageSize: {
                    label   : capitalizeAll(trans('dashboard.label.page_size')),
                    value   : currentUserInputs.pageSize,
                    editable: true,
                    required: false,
                    options : pageSizeInputOptions,
                    hidden  : currentUserInputs.viewType !== AlertsDashboardWidgetViewType.TABULAR,
                    error   : inputErrors.pageSize ? trans(inputErrors.pageSize) : undefined,
                },
                hideColumns: {
                    label                        : capitalizeAll(trans('dashboard.label.hide_columns')),
                    value                        : currentUserInputs.hideColumns ?? [],
                    editable                     : true,
                    options                      : hiddenColumnOptions,
                    multiple                     : true,
                    multipleSelectionDisplayLimit: 10,
                    hidden                       : currentUserInputs.viewType !== AlertsDashboardWidgetViewType.TABULAR,
                    error                        : inputErrors.hideColumns ? trans(inputErrors.hideColumns) : undefined,
                },
            },
        };
    }, [currentUserInputs.entity, currentUserInputs.hideColumns, currentUserInputs.homeId, currentUserInputs.viewType, homeInputOptions,
        iconInputOptions, inputErrors.entity, inputErrors.hideColumns, inputErrors.homeId, inputErrors.icon, inputErrors.title, inputErrors.viewType,
        props.projectId, props.routeContext?.homeId, props.selectedWidget, trans, viewTypeInputOptions, pageSizeInputOptions,
        currentUserInputs.pageSize, inputErrors.pageSize]);



    /**
     * Create and cache the Edit/Save actions for the Edit Alerts dashboard widget component
     */
    const actions = useMemo((): AlertsDashboardWidgetSettingsCompProps['actions'] => {
        return {
            startEdit: {
                onClick: () => {
                    onChangeEditingMode(true);
                },
            },
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: onCancelEdit,
            },
            saveUpdate: {
                label  : isNewWidget ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                onClick: onEditWidgetFormSubmit,
            },
        };
    }, [isNewWidget, onCancelEdit, onChangeEditingMode, onEditWidgetFormSubmit, trans]);



    return (
        <AlertsDashboardWidgetSettingsComp
            className={clsx(classes.widget, props.className, props.infoCompProps?.className, isEditing && 'editing')}
            isEditing={isEditing}
            title={capitalizeAll(trans('label.dashboard_widget_settings'))}
            icon="settings"
            formProps={props.infoCompProps?.formProps}
            fields={dashboardWidgetCompFields}
            actions={actions}
            texts={{
                viewSettings: capitalizeAll(trans('dashboard.labels.view_settings')),
            }}
            onFieldClicked={() => {
                onChangeEditingMode(true);
            }}
            onFormInputChange={onEditWidgetFormInputChange}
        />
    );
};
